import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '../../authentication';
import { validateGroupEasyLink } from '../api/validateGroupEasyLink';
import { acceptForPrefix } from '../api/acceptForPrefix';
import alertService from '../../../services/AlertService';
import { acceptForPrefixAndGroupCode } from '../api/acceptForPrefixAndGroupCode';
import windowService from '../../../services/windowService';
import { User } from '../../authentication';
import { localStorage } from '../../../utils/storageUtils';
import userService from '../../../services/userService';
import { Event } from '../models/event';
import { Assignment, getTrainingOverview, assignTraining, getTrainingItem, TrainingItem } from '../../training';
import { getUserOrgs } from '../../org';
import { filterUserOrgs } from '../../org/utils/filterUserOrgs';
import { syncWhiteLabelOrg } from '../../org/utils/syncWhiteLabelOrg';
import type { UserOrg } from '../../org';

type State = {
  isLoading: boolean;
  errorType?: string | null;
};

type Params = {
  prefix: string;
  groupCode?: string;
};

const getErrorMessage = (errorType?: string | null) => {
  if (errorType === 'NOT_FOUND') {
    return {
      errorHeader: 'Invalid Easy Link',
      errorMessage:
        "Oops! It looks like you've been given a bad link. Reach out to your administrator and validate the url.",
      errorButtonText: 'Skip for Now',
    };
  } else if (errorType === 'Already Exists Error') {
    return {
      errorHeader: "You've Already Used This Easy Link",
      errorMessage: "No need to use this Easy Link again, you're already in.",
      errorButtonText: 'Continue',
    };
  } else if (errorType === 'MembershipCodeDeactivated') {
    return {
      errorHeader: 'This link has been deactivated',
      errorMessage:
        "Oops! It looks like the link you've been given has been deactivated by your administrator. Please contact your administrator to receive a new link.",
      errorButtonText: 'Continue',
    };
  } else {
    return {
      errorHeader: 'Something Went Wrong',
      errorMessage: "Oops! We're sorry for the inconvenience. Please try again.",
      errorButtonText: 'Skip for Now',
    };
  }
};

const getRedirectHashAfterLogin = (user?: User) => {
  if (user && user.redirectURL && user.redirectURL.includes('onboarding')) {
    return user.redirectURL.split('#')[1];
  } else if (user && user.redirectURL && user.redirectURL.includes('#/select-org-and-assign/')) {
    return user.redirectURL.split('#')[1];
  } else if (localStorage.getItem('newHash')) {
    return localStorage.getItem('newHash');
  } else {
    return '/dashboard';
  }
};

export const useAcceptEasyLink = () => {
  const { user, refreshUser } = useUser();
  const { prefix, groupCode } = useParams<Params>();
  const [data, setData] = useState<State>({
    isLoading: true,
    errorType: null,
  });

  const redirectWithOptionalWhiteLabelSync = (sessionUser: User | undefined, loadedOrgs: UserOrg[] = []) => {
    if (sessionUser?.userId && syncWhiteLabelOrg(loadedOrgs, sessionUser)) {
      return;
    }
    windowService.redirectTo(getRedirectHashAfterLogin(sessionUser));
  };

  const redirectHashAndSyncWhiteLabel = (sessionUser = user) => {
    getUserOrgs()
      .then(rawOrgs => {
        redirectWithOptionalWhiteLabelSync(sessionUser, filterUserOrgs(rawOrgs).orgs);
      })
      .catch(() => {
        redirectWithOptionalWhiteLabelSync(sessionUser);
      });
  };

  const redirectToTrainingSteps = (instanceId: string, trainingType: string) => {
    if (trainingType === 'TaskList') {
      windowService.redirectTo(`/training-steps/${instanceId}`);
    } else {
      windowService.redirectTo(`/training-steps/${instanceId}?isWorkflow=true`);
    }
  };

  const redeemEasyLinkAndRedirect = () => {
    if (groupCode && groupCode.length === 4) {
      validateGroupEasyLink(prefix, groupCode)
        .then(() => {
          acceptForPrefixAndGroupCode(prefix, groupCode)
            .then(() => {
              alertService.showOnNextPage('Easy Link Accepted');
              redirectHashAndSyncWhiteLabel();
            })
            .catch(err => setData(prevData => ({ ...prevData, isLoading: false, errorType: err })));
        })
        .catch(() => setData(prevData => ({ ...prevData, isLoading: false, errorType: 'MembershipCodeDeactivated' })));
    } else {
      acceptForPrefix(prefix)
        .then((event: Event) => {
          if (event.code !== 'AlreadyExists') {
            alertService.show('Easy Link Accepted');
          }
          if (user) {
            userService
              .switchOrg(user.userId, event.id)
              .then(() => refreshUser())
              .then(refreshedUser => {
                const activeUser = refreshedUser ?? user;
                if (groupCode && activeUser) {
                  Promise.all([getTrainingItem(groupCode), getTrainingOverview(activeUser.userId, false)])
                    .then(([trainingItem, trainingOverview]: [TrainingItem, Assignment[]]) => {
                      const trainingType = trainingItem.trainingType === 'Workflow' ? 'Workflow' : 'TaskList';
                      const existingAssignedTraining = trainingOverview.find(
                        (existingTraining: Assignment) =>
                          'originalTrainingId' in existingTraining && existingTraining.originalTrainingId === groupCode
                      );

                      if (existingAssignedTraining) {
                        const instanceId = existingAssignedTraining.instanceId;
                        redirectToTrainingSteps(instanceId, trainingType);
                      } else {
                        const userId = activeUser.userId;
                        const orgId = event.id;

                        return assignTraining(
                          trainingItem.trainingId,
                          trainingType === 'Workflow',
                          [userId],
                          orgId,
                          userId,
                          false,
                          null
                        ).then(assignedTraining => {
                          const assignedInstances = assignedTraining.instances;
                          if (assignedInstances.length === 1) {
                            const assignedInstanceId = assignedInstances[0].instanceId;
                            redirectToTrainingSteps(assignedInstanceId, trainingType);
                          }
                        });
                      }
                    })
                    .catch(err => {
                      setData(prevData => ({ ...prevData, isLoading: false, errorType: err }));
                    });
                } else {
                  redirectHashAndSyncWhiteLabel(activeUser);
                }
              })
              .catch(reason => {
                console.error(reason);
                alertService.show('An error occurred. Please try again.', 'error');
              });
          }
        })
        .catch(err => {
          setData(prevData => ({ ...prevData, isLoading: false, errorType: err }));
        });
    }
  };

  useEffect(() => {
    if (user?.userId) {
      redeemEasyLinkAndRedirect();
    }
  }, [user?.userId]);

  const { errorType } = data;
  const { errorHeader, errorMessage, errorButtonText } = getErrorMessage(errorType);

  return {
    ...data,
    errorHeader,
    errorMessage,
    errorButtonText,
    redirectHashAndSyncWhiteLabel,
  };
};
